import React, { useRef, useState } from 'react';
import { UploadCloud, X, FileJson, ClipboardPaste } from 'lucide-react';

/**
 * Reusable .json file uploader with:
 *  - drag & drop
 *  - click to browse
 *  - paste (Ctrl/Cmd + V) — accepts a pasted FILE, or pasted JSON TEXT (synthesized into a File)
 *  - filename + size preview + remove
 *
 * Controlled: pass `file` (File | null) and `onFile(file | null)`.
 * Validation (type / size / JSON.parse) is left to the parent (called via onFile).
 *
 * Paste is scoped to this element (it's focusable) so a stray Ctrl+V elsewhere
 * never hijacks the page — click/focus the zone first, then paste.
 */
const JsonDropzone = ({ file, onFile, disabled = false }) => {
    const [dragOver, setDragOver] = useState(false);
    const inputRef = useRef(null);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        if (disabled) return;
        const f = e.dataTransfer?.files?.[0];
        if (f) onFile(f);
    };

    const handlePaste = (e) => {
        if (disabled) return;
        // 1) A pasted file (e.g. copied from the file explorer)
        const pastedFile = e.clipboardData?.files?.[0];
        if (pastedFile) {
            e.preventDefault();
            onFile(pastedFile);
            return;
        }
        // 2) Pasted JSON text -> synthesize a .json File (parent validates structure)
        const text = e.clipboardData?.getData('text');
        if (text && text.trim()) {
            e.preventDefault();
            const synthesized = new File([text], 'pasted.json', { type: 'application/json' });
            onFile(synthesized);
        }
    };

    const formatSize = (bytes) => {
        if (bytes === undefined || bytes === null) return '';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
    };

    return (
        <div
            tabIndex={disabled ? -1 : 0}
            onPaste={handlePaste}
            onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => !disabled && inputRef.current?.click()}
            className={`relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center outline-none transition-all
                ${dragOver ? 'border-main-blue bg-main-th' : 'border-main-border bg-main-bg hover:border-main-blue/50'}
                focus:border-main-blue focus:ring-4 focus:ring-main-blue/10
                ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            <input
                ref={inputRef}
                type="file"
                accept=".json,application/json"
                className="hidden"
                disabled={disabled}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
            />

            {file instanceof File ? (
                <div className="flex items-center gap-3 w-full max-w-sm">
                    <div className="w-11 h-11 rounded-xl bg-main-th text-main-blue flex items-center justify-center flex-shrink-0">
                        <FileJson size={22} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                        <p className="text-sm font-medium text-main-font truncate">{file.name}</p>
                        <p className="text-xs text-main-subfont">{formatSize(file.size)}</p>
                    </div>
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); onFile(null); }}
                        className="flex-shrink-0 bg-main-delete text-white rounded-full p-1 shadow-md hover:bg-main-delete/90"
                        title="Remove file"
                    >
                        <X size={14} />
                    </button>
                </div>
            ) : (
                <>
                    <div className="w-12 h-12 rounded-xl bg-main-th text-main-blue flex items-center justify-center">
                        <UploadCloud size={22} />
                    </div>
                    <div className="text-xs text-main-subfont leading-relaxed">
                        <span className="font-medium text-main-font">Drag &amp; drop</span> a .json file, click to browse, or{' '}
                        <span className="inline-flex items-center gap-1 align-middle">
                            <ClipboardPaste size={12} /> paste
                        </span>{' '}
                        JSON
                    </div>
                </>
            )}
        </div>
    );
};

export default JsonDropzone;
